import dayjsLib from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { dayjs } from '../../../../shared/utils/date';
import { _Column } from '../importTableDefinition/schema/column';
import { normalizeKana } from './kana';
import { normalizePhoneNumber } from './phoneNumber';

dayjsLib.extend(customParseFormat);

import TransformRule = _Column.TransformRule;

/**
 * 1 つの値に対して transformRules を順番に適用する。
 * string 型カラムは trim を常に先に適用する（UI 設定・transformRules には含めない）。
 *
 * - `padStart`: 指定 length に達するまで先頭を char で埋める
 * - `normalizeDate`: 指定 format で parse して ISO8601(YYYY-MM-DD) に正規化
 * - `normalizeKana`: NFKC + 小書きカナ→通常カナ
 * - `normalizePhoneNumber`: 日本国内電話番号の表記ゆれ除去・先頭 0 復元
 */
export const applyTransformRulesToValue = (value: string, rules: TransformRule[], columnType?: _Column.Type): string => {
  let v = columnType === 'string' ? value.trim() : value;
  for (const rule of rules) {
    switch (rule.kind) {
      case 'normalizeKana':
        v = normalizeKana(v);
        break;
      case 'normalizePhoneNumber':
        v = normalizePhoneNumber(v);
        break;
      case 'padStart':
        v = v.padStart(rule.length, rule.char);
        break;
      case 'normalizeDate': {
        const parsed = dayjs(v, rule.format, true);
        if (parsed.isValid()) {
          v = parsed.format('YYYY-MM-DD');
        }
        break;
      }
    }
  }
  return v;
};

/**
 * 1 行分のレコード（CSV の列名 → 値）に対して schemaJson.columns に従い transformRules を適用する。
 * 入力に存在しない列名はそのまま無視。
 */
export const applyTransformRules = (
  row: Record<string, string>,
  columns: { name: string; type: _Column.Type; transformRules: TransformRule[] }[],
): Record<string, string> => {
  const result: Record<string, string> = { ...row };
  for (const col of columns) {
    const raw = result[col.name];
    if (raw === undefined) continue;
    result[col.name] = applyTransformRulesToValue(raw, col.transformRules, col.type);
  }
  return result;
};

const COMPOSITE_KEY_SEPARATOR = '|';

/** 区切り文字を値に含めても衝突しないようエスケープする */
const escapeCompositeKeyPart = (value: string): string => value.replace(/\\/g, '\\\\').replace(/\|/g, '\\|');

/**
 * 指定された column 名群を順に取り出して `|` 区切りで結合し、複合キー文字列を作る。
 * 値に `|` や `\` が含まれる場合はエスケープしてから結合する（単純 join だと列境界が曖昧になる）。
 * 欠損値は空文字として扱う。
 */
export const buildCompositeKey = (row: Record<string, string>, columnNames: string[]): string => {
  return columnNames.map((name) => escapeCompositeKeyPart(row[name] ?? '')).join(COMPOSITE_KEY_SEPARATOR);
};

type ColumnForDestination = { name: string; destination: _Column.所属先; multiValued?: true };
type CustomerMetadata = Record<string, string | string[]>;

/**
 * row から指定された destination(customer / contract) の列のみを抽出し、
 * `{ [columnName]: value }` の metadata オブジェクトを構築する。
 * customer 向けで `multiValued: true` の列は `[value]` として返す。
 */
export function pickColumnsForDestination(
  row: Record<string, string>,
  columns: ColumnForDestination[],
  destination: 'customer',
): Record<string, string | string[]>;
export function pickColumnsForDestination(
  row: Record<string, string>,
  columns: ColumnForDestination[],
  destination: 'contract',
): Record<string, string>;
export function pickColumnsForDestination(
  row: Record<string, string>,
  columns: ColumnForDestination[],
  destination: 'customer' | 'contract',
): Record<string, string | string[]> {
  return Object.fromEntries(
    columns
      .filter((col) => col.destination === destination)
      .filter((col) => !!row[col.name])
      .map((col) => {
        const value = row[col.name] as string;
        if (destination === 'customer' && col.multiValued) {
          return [col.name, [value]];
        }
        return [col.name, value];
      }),
  );
}

const asMetadataArray = (value: string | string[] | undefined): string[] => {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

const uniqueConcat = (prev: string[], next: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const v of [...prev, ...next]) {
    if (seen.has(v)) continue;
    seen.add(v);
    result.push(v);
  }
  return result;
};

/**
 * 同一 Customer identity の複数行をマージする。
 * multiValued 列は和集合（順序保持・重複排除）、それ以外は last wins。
 */
export const mergeCustomerMetadata = (
  prev: CustomerMetadata,
  next: CustomerMetadata,
  columns: ColumnForDestination[],
): CustomerMetadata => {
  const out: CustomerMetadata = { ...prev };
  for (const col of columns) {
    if (col.destination !== 'customer') continue;
    if (!(col.name in prev) && !(col.name in next)) continue;
    if (col.multiValued) {
      out[col.name] = uniqueConcat(asMetadataArray(prev[col.name]), asMetadataArray(next[col.name]));
    } else {
      out[col.name] = next[col.name] ?? prev[col.name];
    }
  }
  return out;
};
